'use client';

import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import { MapPin, RotateCcw, SquareArrowOutUpRight } from 'lucide-react';
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useState } from 'react';

const MAP_LOAD_TIMEOUT = 10000; // 10 seconds
const GOOGLE_MAPS_URL = 'https://maps.app.goo.gl/jZhKP6QoMzN3tZee6';

const MapComponent = () => {
    const [loadError, setLoadError] = useState(false);
    const [loadTimedOut, setLoadTimedOut] = useState(false);

    const { isLoaded, loadError: scriptLoadError } = useLoadScript({
        // Use the public env var only. Do NOT keep a hard-coded fallback here.
        googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
    });

    // Timeout: if the script hasn't loaded in 10s, show fallback
    useEffect(() => {
        if (isLoaded || loadError) return;
        const timer = setTimeout(() => {
            if (!isLoaded) {
                setLoadTimedOut(true);
            }
        }, MAP_LOAD_TIMEOUT);
        return () => clearTimeout(timer);
    }, [isLoaded, loadError]);

    // Track script load errors
    useEffect(() => {
        if (scriptLoadError) {
            setLoadError(true);
        }
    }, [scriptLoadError]);

    const mapCenter = useMemo(
        () => ({ lat: -37.07679721663818, lng: 174.96440069819664 }),
        [],
    );

    const [map, setMap] = useState(null);
    const [isSpinning, setIsSpinning] = useState(false);

    const onLoad = useCallback((map) => {
        setMap(map);
    }, []);

    const handleReset = useCallback(() => {
        if (map) {
            map.panTo(mapCenter);
            map.setZoom(17);
        }
        setIsSpinning(true);
        setTimeout(() => setIsSpinning(false), 500);
    }, [map, mapCenter]);

    // Show fallback if script failed to load or timed out
    if (loadError || loadTimedOut || (!isLoaded && scriptLoadError)) {
        return (
            <div className="w-full min-h-[300px] h-full bg-gray-100 flex flex-col items-center justify-center rounded-none p-6">
                <MapPin className="w-12 h-12 text-gray-400 mb-4" />
                <p className="text-gray-600 text-center mb-4">
                    Map could not be loaded. View our location on Google Maps.
                </p>
                <Link
                    href={GOOGLE_MAPS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-brand text-white px-6 py-3 rounded-none font-semibold hover:bg-brand/90 transition inline-flex items-center gap-2"
                >
                    Open in Google Maps{' '}
                    <SquareArrowOutUpRight className="w-4 h-4" />
                </Link>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="w-full min-h-[300px] h-full bg-gray-100 flex items-center justify-center">
                <div className="text-gray-500 animate-pulse">
                    Loading map...
                </div>
            </div>
        );
    }

    return (
        <>
            <style>
                {`
            .gm-style-iw-chr {
                color: #f97316 !important;
            }
            .map-container {
                position: relative;
                width: 100%;
                min-height: 300px;
                height: 100%;
            }
            .map-btn-wrapper {
                position: absolute;
                bottom: 16px;
                left: 16px;
                z-index: 10;
                display: flex;
                align-items: flex-end;
                gap: 8px;
            }
            .map-btn {
                background: #f97316;
                color: #fff;
                border: none;
                padding: 0.5rem 1rem;
                border-radius: 4px;
                cursor: pointer;
                font-size: 1rem;
                text-decoration: none;
                transition: background 0.2s;
                box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            }
            .map-reset-btn {
                background: #f97316;
                color: #fff;
                border: none;
                padding: 0.5rem;
                border-radius: 4px;
                cursor: pointer;
                transition: background 0.2s;
                box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                display: flex;
                align-items: center;
                justify-content: center;
            }
            @keyframes spin360 {
                from {
                    transform: rotate(0deg);
                }
                to {
                    transform: rotate(-360deg);
                }
            }
            .spin-icon {
                animation: spin360 0.5s ease-in-out;
            }
        `}
            </style>
            <div className="map-container">
                <GoogleMap
                    mapContainerStyle={{
                        width: '100%',
                        minHeight: '300px',
                        height: '100%',
                    }}
                    center={mapCenter}
                    zoom={17}
                    onLoad={onLoad}
                />
                <div className="map-btn-wrapper">
                    <Link
                        href={GOOGLE_MAPS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="map-btn bg-brand! border-brand! hover:bg-brand-dark! hover:border-brand-dark! h-10"
                    >
                        Google Maps{' '}
                        <SquareArrowOutUpRight className="inline-block w-4 h-4 ml-1" />
                    </Link>
                    <button
                        type="button"
                        onClick={handleReset}
                        className="map-reset-btn bg-brand! border-brand! hover:bg-brand-dark! hover:border-brand-dark! h-10 w-10"
                        title="Reset map view"
                    >
                        <RotateCcw
                            className={`w-5 h-5 ${isSpinning ? 'spin-icon' : ''}`}
                        />
                    </button>
                </div>
            </div>
        </>
    );
};

export default MapComponent;
